import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import {
  DAYS,
  initialForm,
  getTimeDiff,
  formatTotalMinutes,
  extractArray,
} from "./shared";
import { useUserProfile } from "../../../../../../../hooks/userProfile";
import { getActiveCarePlans } from "../../../../../../../api/services/carePathWay";
import {
  getCheckList,
  getClientPaymentServices,
} from "../../../../../../../api/services/core";
import { createVisit } from "../../../../../../../api/services/scheduling";
import { QK } from "../../../../../../../base/qk";
import { VisitTypes } from "../../visit.config";

const getTodayInTimezone = (timezone) => {
  const now = new Date();
  const dateFormatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const weekdayFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "short",
  });
  return {
    date: dateFormatter.format(now),
    weekday: weekdayFormatter.format(now).substring(0, 3),
  };
};

const useCallVisitForm = (onClose, onCreated) => {
  const queryClient = useQueryClient();
  const { id: clientID } = useParams();
  const user = useUserProfile();
  const userTimezone = user?.business?.timezone || "Europe/London";

  const [formData, setFormData] = useState(initialForm);
  const [isCustomTime, setIsCustomTime] = useState(false);
  const [customTimeData, setCustomTimeData] = useState({});
  const [totalWeeklyHours, setTotalWeeklyHours] = useState("0h 0m");
  const [isTimeCritical, setIsTimeCritical] = useState(false);
  const [isContinueIndifinite, setIsContinueIndifinite] = useState(false);
  const [showFundingModal, setShowFundingModal] = useState(false);
  const [showCarePlanDialog, setShowCarePlanDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [visitType, setVisitType] = useState("");
  const [selectedDays, setSelectedDays] = useState([]);
  const [selectedCheckList, setSelectedCheckList] = useState([]);
  const [createdVisitId, setCreatedVisitId] = useState(null);
  const [createdArea, setCreatedArea] = useState(null);

  const {
    data: activeCarePlans = [],
    isLoading: isLoadingCarePlans,
    refetch: refetchActiveCarePlans,
  } = useQuery(
    [QK.activeCarePlans, { client: clientID }],
    () => getActiveCarePlans({ client: clientID }),
    { enabled: !!clientID }
  );

  const { data: checkList } = useQuery([QK.checklist], () => getCheckList());
  const { data: paymentServices } = useQuery(
    [QK.fundingProfiles, clientID],
    () => getClientPaymentServices(clientID),
    { enabled: !!clientID }
  );

  useEffect(() => {
    if (user) {
      const { date, weekday } = getTodayInTimezone(userTimezone);
      setFormData((prev) => ({
        ...prev,
        start_time: `${date}T00:00`,
      }));
      setSelectedDays([weekday]);
      setCustomTimeData({
        [weekday]: [{ time_required_from: "", time_required_to: "" }],
      });
    }
  }, [user, userTimezone]);

  useEffect(() => {
    if (activeCarePlans && formData.care_plan) {
      const exists = activeCarePlans.some(
        (p) => String(p.id) === String(formData.care_plan)
      );
      if (!exists) {
        const matched = activeCarePlans.find(
          (p) => String(p.form) === String(formData.care_plan)
        );
        if (matched) {
          setFormData((prev) => ({ ...prev, care_plan: matched.id }));
        } else {
          setFormData((prev) => ({ ...prev, care_plan: "" }));
        }
      }
    }
  }, [activeCarePlans]);

  const carePlanOptions = activeCarePlans.map((p) => ({
    value: p.id,
    label: p.form_title || p.title || p.name || `Care Plan ${p.id}`,
  }));

  const paymentServiceOptions = extractArray(paymentServices).map((p) => ({
    value: p.id || p.value,
    label: p.service || p.name || p.label || `Service ${p.id}`,
  }));

  const checkListOptions = extractArray(checkList).map((c) => ({
    value: c.id || c.value,
    label: c.name || c.label || `Check ${c.id}`,
  }));

  const visitTypeOptions = (VisitTypes || []).map((v) => ({
    value: v.id || v.value,
    label: v.label || v.name || `Type ${v.id}`,
  }));

  const calculateTotalWeeklyHours = (timeSource) => {
    let totalMinutes = 0;

    if (isCustomTime) {
      Object.values(timeSource).forEach((slots) => {
        if (Array.isArray(slots)) {
          slots.forEach((slot) => {
            if (slot.time_required_from && slot.time_required_to) {
              totalMinutes += getTimeDiff(
                slot.time_required_from,
                slot.time_required_to
              );
            }
          });
        }
      });
    } else if (formData.time_required_from && formData.time_required_to) {
      const dailyDiff = getTimeDiff(
        formData.time_required_from,
        formData.time_required_to
      );
      totalMinutes = dailyDiff * selectedDays.length;
    }

    setTotalWeeklyHours(formatTotalMinutes(totalMinutes));
    setFormData((prev) => ({ ...prev, total_duration: totalMinutes }));
  };

  useEffect(() => {
    calculateTotalWeeklyHours(customTimeData);
  }, [selectedDays, isCustomTime]);

  const handleChange = (e) => {
    const { name, checked, type, value } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    const finalValue = type === "number" ? Number(value) : newValue;
    setFormData((prev) => ({ ...prev, [name]: finalValue }));

    if (name === "start_time" && value) {
      const datePart = value.split("T")[0];
      const dayMap = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const weekday = dayMap[new Date(`${datePart}T12:00:00`).getDay()];
      setSelectedDays([weekday]);
      setCustomTimeData({
        [weekday]: [{ time_required_from: "", time_required_to: "" }],
      });
    }

    if (name === "time_required_from" || name === "time_required_to") {
      if (!isCustomTime) calculateTotalWeeklyHours(customTimeData);
    }
  };

  const handleCheckBox = (name, checked) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));
    if (name === "time_critical") setIsTimeCritical(checked);
    if (name === "continue_indefinitely") {
      setIsContinueIndifinite(checked);
      setFormData((prev) => ({ ...prev, end_time: "" }));
    }
  };

  const handleVisitTypeChange = (e) => {
    const val = Number(e.target.value);
    setVisitType(val);
    setFormData((prev) => ({ ...prev, repeat_every: val }));
    if (val === 1) {
      setSelectedDays(DAYS);
    } else {
      const { weekday } = getTodayInTimezone(userTimezone);
      setSelectedDays([weekday]);
      setCustomTimeData({
        [weekday]: [{ time_required_from: "", time_required_to: "" }],
      });
    }
  };

  const handleDaySelection = (day) => {
    setSelectedDays((prev) => {
      const isSelected = prev.includes(day);
      const updatedDays = isSelected
        ? prev.filter((d) => d !== day)
        : [...prev, day];

      const newTimeData = { ...customTimeData };
      if (isSelected) {
        delete newTimeData[day];
      } else {
        newTimeData[day] = [{ time_required_from: "", time_required_to: "" }];
      }

      setCustomTimeData(newTimeData);
      return updatedDays;
    });
  };

  const handleCustomTimeChange = (day, index, key, value) => {
    const slots = Array.isArray(customTimeData[day])
      ? [...customTimeData[day]]
      : [];
    slots[index] = { ...slots[index], [key]: value };
    const newData = { ...customTimeData, [day]: slots };
    setCustomTimeData(newData);
    calculateTotalWeeklyHours(newData);
  };

  const handleAddSlot = (day) => {
    const currentSlots = Array.isArray(customTimeData[day])
      ? customTimeData[day]
      : [];
    const newData = {
      ...customTimeData,
      [day]: [
        ...currentSlots,
        { id: null, time_required_from: "", time_required_to: "" },
      ],
    };
    setCustomTimeData(newData);
    calculateTotalWeeklyHours(newData);
  };

  const handleRemoveSlot = (day, index) => {
    const slots = Array.isArray(customTimeData[day]) ? customTimeData[day] : [];
    const newSlots = slots.filter((_, i) => i !== index);
    const newData = { ...customTimeData, [day]: newSlots };

    if (newSlots.length === 0) {
      delete newData[day];
      setSelectedDays((prev) => prev.filter((d) => d !== day));
    }
    setCustomTimeData(newData);
    calculateTotalWeeklyHours(newData);
  };

  const handleAddInput = () => {
    setFormData((prev) => ({
      ...prev,
      personalized_checks: [
        ...(prev.personalized_checks || []),
        { id: Date.now(), check_name: "" },
      ],
    }));
  };

  const handleRemoveInput = (id) => {
    setFormData((prev) => ({
      ...prev,
      personalized_checks: prev.personalized_checks.filter(
        (c) => c.id !== id
      ),
    }));
  };

  const handlePersonalizedCheck = (index, e) => {
    const value = e.target.value;
    setFormData((prev) => {
      const newChecks = [...prev.personalized_checks];
      newChecks[index].check_name = value;
      return { ...prev, personalized_checks: newChecks };
    });
  };

  const handleCarePlanSelect = (formId) => {
    const matchedPlan = activeCarePlans?.find((p) => p.form === formId);
    if (matchedPlan) {
      setFormData((prev) => ({ ...prev, care_plan: matchedPlan.id }));
    } else {
      refetchActiveCarePlans();
      setFormData((prev) => ({ ...prev, care_plan: formId }));
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setIsSubmitting(true);

    let weeklyTimeList = [];

    if (isCustomTime) {
      selectedDays.forEach((day) => {
        const slots = customTimeData[day] || [];
        slots.forEach((slot) => {
          if (slot.time_required_from && slot.time_required_to) {
            weeklyTimeList.push({
              [day]: {
                time_required_from: slot.time_required_from,
                time_required_to: slot.time_required_to,
              },
            });
          }
        });
      });
    } else {
      weeklyTimeList = selectedDays.map((day) => ({
        [day]: {
          time_required_from: formData.time_required_from || "00:00",
          time_required_to: formData.time_required_to || "00:00",
        },
      }));
    }

    const requestData = {
      ...formData,
      time_required_from: formData.time_required_from || "00:00",
      time_required_to: formData.time_required_to || "00:00",
      visit_type: 2,
      recurrence_type: visitType === 2 ? 2 : 1,
      weekly_time_data: weeklyTimeList,
      user: user?.id,
      client: clientID,
      checklist: selectedCheckList?.map((item) => item.value),
      start_time: formData.start_time
        ? new Date(formData.start_time).toISOString()
        : null,
      end_time:
        formData.end_time && !isContinueIndifinite
          ? new Date(formData.end_time).toISOString()
          : null,
    };

    try {
      const response = await createVisit(requestData);
      if (response.status === 201) {
        enqueueSnackbar("Visit created successfully.", { variant: "success" });
        queryClient.invalidateQueries(QK.visits);
        setCreatedVisitId(response.data.id);
        setCreatedArea(response.data.area);
        onCreated?.();
      }
    } catch (error) {
      enqueueSnackbar("Error creating Visit. Please check your inputs.", {
        variant: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    isCustomTime,
    setIsCustomTime,
    customTimeData,
    totalWeeklyHours,
    isTimeCritical,
    isContinueIndifinite,
    showFundingModal,
    setShowFundingModal,
    showCarePlanDialog,
    setShowCarePlanDialog,
    isSubmitting,
    selectedDays,
    selectedCheckList,
    setSelectedCheckList,
    carePlanOptions,
    paymentServiceOptions,
    checkListOptions,
    visitTypeOptions,
    isLoadingCarePlans,
    clientID,
    createdVisitId,
    createdArea,
    handleChange,
    handleCheckBox,
    handleVisitTypeChange,
    handleDaySelection,
    handleCustomTimeChange,
    handleAddSlot,
    handleRemoveSlot,
    handleAddInput,
    handleRemoveInput,
    handlePersonalizedCheck,
    handleCarePlanSelect,
    handleSubmit,
  };
};

export default useCallVisitForm;